/**
 * Custom hooks for managing client type tags relationships
 */

import { useState, useEffect, useCallback } from 'react';
import { clientTypeTagsService } from '../services/clientTypeTagsService';
import { useAccessToken } from './useAuth';
import { useApiErrorHandler } from './useErrorHandling';
import { useAlert } from '../context/AlertContext';

/**
 * Hook for managing all client type tags relationships
 * @returns {Object} Client type tags data and methods
 */
export function useClientTypeTags() {
    const [relationships, setRelationships] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getBackendAccessToken } = useAccessToken();
    const { handleError, withErrorHandling } = useApiErrorHandler();

    const loadAllRelationships = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        await withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const data = await clientTypeTagsService.getAll(accessToken);
                setRelationships(data || []);
            },
            'load relationships',
            {
                onError: (err, message) => setError(message)
            }
        );
        
        setLoading(false);
    }, [getBackendAccessToken, withErrorHandling]);

    const createRelationship = useCallback(async (clientId, clientTypeId) => {
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const newRelationship = await clientTypeTagsService.create(accessToken, { clientId, clientTypeId });
                setRelationships(prev => [...prev, newRelationship]);
                return newRelationship;
            },
            'create relationship'
        );
    }, [getBackendAccessToken, withErrorHandling]);

    const deleteRelationship = useCallback(async (clientId, clientTypeId) => {
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                await clientTypeTagsService.delete(accessToken, clientId, clientTypeId);
                setRelationships(prev => 
                    prev.filter(rel => !(rel.clientId === clientId && rel.clientTypeId === clientTypeId))
                );
            },
            'delete relationship'
        );
    }, [getBackendAccessToken, withErrorHandling]);

    const toggleRelationship = useCallback(async (clientId, clientTypeId) => {
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const result = await clientTypeTagsService.toggleClientTypeForClient(accessToken, clientId, clientTypeId);
                
                if (result.action === 'added') {
                    setRelationships(prev => [...prev, result.data]);
                } else {
                    setRelationships(prev => 
                        prev.filter(rel => !(rel.clientId === clientId && rel.clientTypeId === clientTypeId))
                    );
                }
                
                return result;
            },
            'toggle relationship'
        );
    }, [getBackendAccessToken, withErrorHandling]);

    return {
        relationships,
        loading,
        error,
        loadAllRelationships,
        createRelationship,
        deleteRelationship,
        toggleRelationship,
    };
}

/**
 * Hook for managing client type assignments for a specific client
 * @param {number} clientId - Client ID
 * @param {Array} [preloadedAssignments] - Pre-loaded assignments to avoid initial API call
 * @returns {Object} Client type assignments data and methods
 */
export function useClientTypeAssignments(clientId, preloadedAssignments = null) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [count, setCount] = useState(0);
    const { getBackendAccessToken } = useAccessToken();
    const { handleError, withErrorHandling, handleSuccess } = useApiErrorHandler();

    const loadAssignments = useCallback(async () => {
        if (!clientId) {
            setAssignments([]);
            setCount(0);
            return;
        }
        
        // OPTIMIZATION: Use preloaded assignments if available
        if (preloadedAssignments) {
            setAssignments(preloadedAssignments);
            setCount(preloadedAssignments.length);
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            const accessToken = await getBackendAccessToken();
            const [data, countData] = await Promise.all([
                clientTypeTagsService.getClientTypeAssignmentsWithDetails(accessToken, clientId),
                clientTypeTagsService.getClientTypeCountForClient(accessToken, clientId)
            ]);
            setAssignments(data || []);
            setCount(countData || 0);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load assignments';
            setError(errorMessage);
            console.error('Error loading client type assignments:', err);
            // Don't show error alert for initial load
        } finally {
            setLoading(false);
        }
    }, [clientId, preloadedAssignments, getBackendAccessToken]);

    const updateAssignments = useCallback(async (clientTypeIds) => {
        if (!clientId) return [];
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const updatedAssignments = await clientTypeTagsService.updateClientTypesForClient(
                    accessToken, 
                    clientId, 
                    clientTypeIds
                );
                setAssignments(updatedAssignments || []);
                setCount(updatedAssignments?.length || 0);
                handleSuccess('Client type assignments updated successfully');
                return updatedAssignments;
            },
            'update assignments',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientId, getBackendAccessToken, withErrorHandling, handleSuccess]);

    const addAssignments = useCallback(async (clientTypeIds) => {
        if (!clientId || !clientTypeIds?.length) return [];
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const newAssignments = await clientTypeTagsService.assignClientTypesToClient(
                    accessToken, 
                    clientId, 
                    clientTypeIds
                );
                setAssignments(prev => [...prev, ...(newAssignments || [])]);
                setCount(prev => prev + (newAssignments?.length || 0));
                handleSuccess(`Added ${newAssignments?.length || 0} client type(s)`);
                return newAssignments;
            },
            'add assignments',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientId, getBackendAccessToken, withErrorHandling, handleSuccess]);

    const addSingleAssignment = useCallback(async (clientTypeId) => {
        if (!clientId || !clientTypeId) return null;
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const newAssignment = await clientTypeTagsService.addClientTypeToClient(
                    accessToken, 
                    clientId, 
                    clientTypeId
                );
                setAssignments(prev => [...prev, newAssignment]);
                setCount(prev => prev + 1);
                return newAssignment;
            },
            'add client type',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientId, getBackendAccessToken, withErrorHandling]);

    const removeSingleAssignment = useCallback(async (clientTypeId) => {
        if (!clientId || !clientTypeId) return;
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                await clientTypeTagsService.removeClientTypeFromClient(
                    accessToken, 
                    clientId, 
                    clientTypeId
                );
                setAssignments(prev => prev.filter(a => a.clientTypeId !== clientTypeId));
                setCount(prev => Math.max(0, prev - 1));
            },
            'remove client type',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientId, getBackendAccessToken, withErrorHandling]);

    const toggleAssignment = useCallback(async (clientTypeId) => {
        if (!clientId || !clientTypeId) return null;
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const result = await clientTypeTagsService.toggleClientTypeForClient(
                    accessToken, 
                    clientId, 
                    clientTypeId
                );
                
                if (result.action === 'added') {
                    setAssignments(prev => [...prev, result.data]);
                    setCount(prev => prev + 1);
                } else {
                    setAssignments(prev => prev.filter(a => a.clientTypeId !== clientTypeId));
                    setCount(prev => Math.max(0, prev - 1));
                }
                
                return result;
            },
            'toggle assignment',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientId, getBackendAccessToken, withErrorHandling]);

    const removeAllAssignments = useCallback(async () => {
        if (!clientId) return;
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                await clientTypeTagsService.deleteAllClientTypesForClient(accessToken, clientId);
                setAssignments([]);
                setCount(0);
                handleSuccess('All client type assignments removed');
            },
            'remove all assignments',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientId, getBackendAccessToken, withErrorHandling, handleSuccess]);

    const isAssigned = useCallback((clientTypeId) => {
        return assignments.some(a => a.clientTypeId === clientTypeId);
    }, [assignments]);

    const getAssignment = useCallback((clientTypeId) => {
        return assignments.find(a => a.clientTypeId === clientTypeId);
    }, [assignments]);

    // Load assignments when clientId changes
    useEffect(() => {
        loadAssignments();
    }, [loadAssignments]);

    return {
        assignments,
        loading,
        error,
        count,
        loadAssignments,
        updateAssignments,
        addAssignments,
        addSingleAssignment,
        removeSingleAssignment,
        toggleAssignment,
        removeAllAssignments,
        isAssigned,
        getAssignment,
        refreshAssignments: loadAssignments,
    };
}

/**
 * Hook for managing client assignments for a specific client type
 * @param {number} clientTypeId - Client type ID
 * @returns {Object} Client assignments data and methods
 */
export function useClientAssignments(clientTypeId) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [count, setCount] = useState(0);
    const { getBackendAccessToken } = useAccessToken();
    const { handleError, withErrorHandling, handleSuccess } = useApiErrorHandler();

    const loadAssignments = useCallback(async () => {
        if (!clientTypeId) {
            setAssignments([]);
            setCount(0);
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            const accessToken = await getBackendAccessToken();
            const [data, countData] = await Promise.all([
                clientTypeTagsService.getClientsForClientType(accessToken, clientTypeId),
                clientTypeTagsService.getClientCountForClientType(accessToken, clientTypeId)
            ]);
            setAssignments(data || []);
            setCount(countData || 0);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load assignments';
            setError(errorMessage);
            console.error('Error loading client assignments:', err);
            // Don't show error alert for initial load
        } finally {
            setLoading(false);
        }
    }, [clientTypeId, getBackendAccessToken]);

    const updateAssignments = useCallback(async (clientIds) => {
        if (!clientTypeId) return [];
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const updatedAssignments = await clientTypeTagsService.updateClientsForClientType(
                    accessToken, 
                    clientTypeId, 
                    clientIds
                );
                setAssignments(updatedAssignments || []);
                setCount(updatedAssignments?.length || 0);
                handleSuccess('Client assignments updated successfully');
                return updatedAssignments;
            },
            'update assignments',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientTypeId, getBackendAccessToken, withErrorHandling, handleSuccess]);

    const addAssignments = useCallback(async (clientIds) => {
        if (!clientTypeId || !clientIds?.length) return [];
        
        return withErrorHandling(
            async () => {
                const accessToken = await getBackendAccessToken();
                const newAssignments = await clientTypeTagsService.assignClientsToClientType(
                    accessToken, 
                    clientTypeId, 
                    clientIds
                );
                setAssignments(prev => [...prev, ...(newAssignments || [])]);
                setCount(prev => prev + (newAssignments?.length || 0));
                handleSuccess(`Added ${newAssignments?.length || 0} client(s)`);
                return newAssignments;
            },
            'add assignments',
            {
                onError: (err, message) => setError(message)
            }
        );
    }, [clientTypeId, getBackendAccessToken, withErrorHandling, handleSuccess]);

    // Load assignments when clientTypeId changes
    useEffect(() => {
        loadAssignments();
    }, [loadAssignments]);

    return {
        assignments,
        loading,
        error,
        count,
        loadAssignments,
        updateAssignments,
        addAssignments,
        refreshAssignments: loadAssignments,
    };
}
